import React, { useState } from 'react';
import { X, ChevronRight } from 'lucide-react';

import CaregiverSetupQR from './CaregiverSetupQR';

/**
 * Caregiver setup-credentials success modal (FECOA-1428/1429/1430).
 *
 * Shown after one or more caregivers are created with an in-clinic temporary
 * PIN, so the SC can share each caregiver's setup QR / ID / PIN during the
 * onboarding session.
 *
 *  - 1 caregiver  → single centered card (FECOA-1429 / 1430)
 *  - 2+ caregivers → left selector list + selected caregiver's QR (FECOA-1428)
 *
 * Privacy (FECOA-1428 note): the multi-caregiver selector masks other
 * caregivers to "CG-ID · Initials" so a caregiver facing the device can't read
 * another's full legal name. The selected caregiver's full name still shows on
 * their own card, and "Enlarge for Scanning" hides everything but the QR.
 *
 * Props:
 *   - caregivers: [{ caregiverId, tempPin, name, relationship }]
 *   - studyCode: the participant's Study Code, which caregivers sign in with
 *     (FECOA-2156)
 *   - sponsorId: only used for the setup link when the study code is unknown
 *   - onClose
 */
const initialsOf = (name) => {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean)
    .join('.')
    .concat('.');
};

const CaregiverSetupModal = ({ caregivers = [], studyCode, sponsorId, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMulti = caregivers.length > 1;
  const selected = caregivers[selectedIndex] || caregivers[0];

  if (!caregivers.length) return null;

  const card = selected && (
    <div className="bg-gray-50 dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-lg p-5">
      <CaregiverSetupQR
        caregiverId={selected.caregiverId}
        tempPin={selected.tempPin}
        studyCode={studyCode}
        sponsorId={sponsorId}
        name={selected.name}
        relationship={selected.relationship}
      />
    </div>
  );

  const instructions = (
    <div className="mt-4 bg-gray-50 dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-md p-3">
      <p className="text-xs text-gray-600 dark:text-gray-300">
        <strong className="text-gray-900 dark:text-white">Instructions:</strong>{' '}
        {studyCode
          ? 'The caregiver can scan this QR code, or sign in on the flexCOA app or website with the Study Code, Caregiver ID and Temporary PIN to complete their setup.'
          : 'The caregiver can scan this QR code or manually enter the Caregiver ID and Temporary PIN to complete their setup.'}
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-lg w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-primary-500 text-white flex-shrink-0">
          <h2 className="text-lg font-semibold">
            {isMulti ? 'Caregivers Setup Ready' : 'Caregiver Setup Ready'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="hover:opacity-80">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isMulti ? (
            <div className="grid grid-cols-1 sm:grid-cols-3">
              {/* Left: masked selector list (FECOA-1428 privacy) */}
              <div className="sm:col-span-1 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-slate-700 p-4">
                <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-3">
                  {caregivers.length} Caregivers Created
                </p>
                <div className="space-y-2">
                  {caregivers.map((cg, index) => {
                    const isActive = index === selectedIndex;
                    return (
                      <button
                        key={cg.caregiverId}
                        type="button"
                        onClick={() => setSelectedIndex(index)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left ${
                          isActive
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-mono font-semibold truncate">
                            {cg.caregiverId}
                          </p>
                          <p
                            className={`text-xs truncate ${
                              isActive ? 'text-primary-50' : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {initialsOf(cg.name) || '—'}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: selected caregiver detail */}
              <div className="sm:col-span-2 p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                  {caregivers.length} caregiver accounts created successfully. Share each
                  caregiver&rsquo;s setup information below.
                </p>
                {card}
                {instructions}
              </div>
            </div>
          ) : (
            <div className="p-5">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                Caregiver account created successfully. Share this information with the caregiver to
                complete setup.
              </p>
              {card}
              {instructions}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaregiverSetupModal;
